import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ProductsSiteLayout from '../../../LandingPage/products/ProductsSiteLayout';
import { useProductsTheme } from '../../../LandingPage/products/useProductsTheme';
import { buildSigninUrl } from '../../../newsintelligence/support/openDiscussions';
import { useSessionAuth } from '../../../utils/SessionAuthContext';
import { peekSessionCache } from '../../../utils/sessionAuth';
import AudioPlayerLanding from './AudioPlayerLanding';
import LocalDeviceAudioSession from './LocalDeviceAudioSession';
import { useGlobalStorageBoxAudio } from './globalStorageBoxAudioSession';
import StreamAudioSession from './StreamAudioSession';
import AudioPlayer from './AudioPlayer';
import { deleteOfflineTrack, listOfflineTracks } from './audioOfflineStorage';
import { findStreamByUrl } from './webStreamCatalog';
import { useLocalDevicePlaylist } from './useLocalDevicePlaylist';
import { TbHeadphones } from 'react-icons/tb';
import { FiDownload, FiFolder, FiHardDrive, FiRadio } from 'react-icons/fi';
import './AudioPlayer.css';

const TABS = [
  { id: 'listen', label: 'Listen', Icon: FiRadio },
  { id: 'device', label: 'On this device', Icon: FiFolder },
  { id: 'storagebox', label: 'Storage Box', Icon: FiHardDrive },
  { id: 'saved', label: 'Saved offline', Icon: FiDownload },
];

function scrollAudioPlayerPageToTop() {
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  if (document.documentElement) document.documentElement.scrollTop = 0;
  if (document.body) document.body.scrollTop = 0;
}

export default function AudioPlayerProductPage() {
  const location = useLocation();
  const { isDark } = useProductsTheme();
  const { status } = useSessionAuth();
  const authed = status === 'authenticated' || peekSessionCache();
  const theme = isDark ? 'dark' : 'light';

  const [tab, setTab] = useState('listen');
  const [streamUrl, setStreamUrl] = useState('');
  const [streamTitle, setStreamTitle] = useState('Web stream');
  const [urlDraft, setUrlDraft] = useState('');
  const [playlistBootstrap, setPlaylistBootstrap] = useState(null);
  const [savedTracks, setSavedTracks] = useState([]);
  const [activeSavedKey, setActiveSavedKey] = useState('');
  const [savedBlobUrl, setSavedBlobUrl] = useState('');
  const devicePlaylist = useLocalDevicePlaylist();
  const fileInputRef = useRef(null);
  const storageInlineHostRef = useRef(null);
  const {
    isActive: storageSessionActive,
    registerInlineHost,
    loadStorageBoxPlaylist,
    setMinimized: setStorageSessionMinimized,
  } = useGlobalStorageBoxAudio();

  useLayoutEffect(() => {
    if (location.pathname !== '/audio-player') return;
    scrollAudioPlayerPageToTop();
  }, [location.pathname, location.key]);

  const refreshSaved = useCallback(async () => {
    try {
      const rows = await listOfflineTracks();
      setSavedTracks(rows);
    } catch {
      setSavedTracks([]);
    }
  }, []);

  useEffect(() => {
    refreshSaved();
  }, [refreshSaved]);

  useEffect(() => {
    let revoke = null;
    async function loadSaved() {
      if (!activeSavedKey) {
        setSavedBlobUrl('');
        return;
      }
      const row = savedTracks.find((r) => r.key === activeSavedKey);
      if (!row?.blob) {
        setSavedBlobUrl('');
        return;
      }
      revoke = URL.createObjectURL(row.blob);
      setSavedBlobUrl(revoke);
    }
    loadSaved();
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [activeSavedKey, savedTracks]);

  const streamSessionKey = useMemo(() => {
    if (playlistBootstrap?.id) return `pl-${playlistBootstrap.id}`;
    return streamUrl || 'idle';
  }, [playlistBootstrap?.id, streamUrl]);

  const signinHref = buildSigninUrl({ returnTo: '/audio-player' });

  const startStream = useCallback((url, title = 'Web stream') => {
    const trimmed = String(url || '').trim();
    if (!trimmed) return;
    setPlaylistBootstrap(null);
    setStreamUrl(trimmed);
    setStreamTitle(title);
    setUrlDraft(trimmed);
    setTab('listen');
  }, []);

  const loadSavedPlaylist = useCallback((row, urlTracks) => {
    setStreamUrl(urlTracks[0]?.url || '');
    setStreamTitle(urlTracks[0]?.title || 'Web stream');
    setUrlDraft(urlTracks[0]?.url || '');
    setPlaylistBootstrap({
      id: row.id,
      tracks: urlTracks,
      meta: { id: row.id, name: row.name, shuffle: row.shuffle, repeat: row.repeat, eqGains: row.eqGains },
    });
    setTab('listen');
  }, []);

  const startStorageBoxPlaylist = useCallback(
    (row, storageTracks) => {
      setStreamUrl('');
      setPlaylistBootstrap(null);
      setUrlDraft('');
      loadStorageBoxPlaylist(row, storageTracks);
      setTab('storagebox');
    },
    [loadStorageBoxPlaylist]
  );

  useEffect(() => {
    if (tab === 'storagebox' && authed) {
      registerInlineHost(storageInlineHostRef.current);
    } else {
      registerInlineHost(null);
    }
    return () => registerInlineHost(null);
  }, [tab, authed, registerInlineHost]);

  useLayoutEffect(() => {
    if (tab === 'storagebox' && authed) {
      registerInlineHost(storageInlineHostRef.current);
    }
  }, [tab, authed, registerInlineHost, storageSessionActive]);

  useEffect(() => {
    if (tab !== 'storagebox' && storageSessionActive) {
      setStorageSessionMinimized(true);
    }
  }, [tab, storageSessionActive, setStorageSessionMinimized]);

  return (
    <ProductsSiteLayout activeNav="products" mainClassName="audio-player-page">
      <header className="audio-player-page__hero products-intro">
        <p className="products-intro-eyebrow">Media</p>
        <div className="audio-player-page__title-row">
          <span className="audio-player-page__title-icon" aria-hidden>
            <TbHeadphones />
          </span>
          <h1 className="products-intro-title audio-player-page__title">Audio Player</h1>
        </div>
      </header>

      <form
        className="audio-player-page__url-shell audio-player-page__url-form audio-player-page__url-form--hero"
        onSubmit={(e) => {
          e.preventDefault();
          if (tab !== 'listen') setTab('listen');
          const meta = findStreamByUrl(urlDraft.trim());
          startStream(urlDraft.trim(), meta?.title || 'Web stream');
        }}
      >
        <input
          type="url"
          placeholder="Paste a direct stream or audio URL…"
          value={urlDraft}
          onChange={(e) => setUrlDraft(e.target.value)}
          aria-label="Audio URL"
        />
        <button type="submit" className="audio-player-page__url-load-btn">
          Load
        </button>
      </form>

      <div
        className="audio-player-page__tabs audio-player-page__segment"
        role="tablist"
        aria-label="Audio player sources"
      >
        {TABS.map((t) => {
          const TabIcon = t.Icon;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              data-tab={t.id}
              className={`audio-player-page__tab${tab === t.id ? ' audio-player-page__tab--active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              <span className="audio-player-page__tab-inner">
                <TabIcon className="audio-player-page__tab-icon" aria-hidden />
                <span className="audio-player-page__tab-label">{t.label}</span>
              </span>
            </button>
          );
        })}
      </div>

      {tab === 'listen' ? (
        <div className="audio-player-page__panel audio-player-page__panel--listen">
          {streamUrl ? (
            <div className="audio-player-page__listen-now-playing">
              <div className="audio-player-page__now-playing-bar">
                <button
                  type="button"
                  className="audio-player-page__back-to-browse"
                  onClick={() => {
                    setStreamUrl('');
                    setPlaylistBootstrap(null);
                    setUrlDraft('');
                  }}
                >
                  Close player
                </button>
                <span className="audio-player-page__now-playing-label" title={streamUrl}>
                  {streamTitle}
                </span>
              </div>
              <StreamAudioSession
                key={streamSessionKey}
                streamUrl={streamUrl}
                theme={theme}
                title={streamTitle}
                bootstrapTracks={playlistBootstrap?.tracks}
                bootstrapMeta={playlistBootstrap?.meta}
              />
            </div>
          ) : null}
          <AudioPlayerLanding
            authed={authed}
            onPlayStream={(url, title) => startStream(url, title)}
            onLoadSavedPlaylist={loadSavedPlaylist}
            onLoadStorageBoxPlaylist={startStorageBoxPlaylist}
          />
        </div>
      ) : null}

      {tab === 'device' ? (
        <div className="audio-player-page__panel">
          <div className="audio-player-page__device-import">
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*,.mp3,.m4a,.wav,.flac,.ogg,.opus"
              multiple
              className="audio-player-page__file-input"
              onChange={(e) => {
                devicePlaylist.addFilesFromPicker(e.target.files);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              className="audio-player-page__device-import-btn"
              onClick={() => fileInputRef.current?.click()}
            >
              Add files from this device
            </button>
            <p className="audio-player-page__device-hint">MP3, M4A, WAV, and other supported formats.</p>
          </div>

          <LocalDeviceAudioSession theme={theme} playlistApi={devicePlaylist} />
        </div>
      ) : null}

      {tab === 'saved' ? (
        <div className="audio-player-page__panel">
          {savedTracks.length === 0 ? (
            <p className="audio-player__message">
              No offline tracks yet. Use Download for offline while streaming or playing from Storage Box.
            </p>
          ) : (
            <ul className="audio-player-page__saved-list">
              {savedTracks.map((row) => (
                <li key={row.key} className="audio-player-page__saved-row">
                  <span>{row.title}</span>
                  <span className="audio-player-page__saved-row-actions">
                    <button type="button" onClick={() => setActiveSavedKey(row.key)}>
                      Play
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        await deleteOfflineTrack(row.key);
                        if (activeSavedKey === row.key) setActiveSavedKey('');
                        refreshSaved();
                      }}
                    >
                      Delete
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {activeSavedKey && savedBlobUrl ? (
            <AudioPlayer
              theme={theme}
              src={savedBlobUrl}
              title={savedTracks.find((r) => r.key === activeSavedKey)?.title || 'Offline track'}
              subtitle="Device storage"
              offlineKey={activeSavedKey}
              allowStreamToggle={false}
            />
          ) : null}
        </div>
      ) : null}

      {tab === 'storagebox' ? (
        <div className="audio-player-page__panel audio-player-page__panel--storagebox">
          {!authed ? (
            <p className="audio-player__message">
              <Link to={signinHref}>Sign in</Link> to browse Storage Box and stream your audio files.
            </p>
          ) : (
            <div ref={storageInlineHostRef} className="audio-player-page__storage-inline-host" />
          )}
        </div>
      ) : null}
    </ProductsSiteLayout>
  );
}
